import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { api } from '@/lib/api';

interface HourlyActivityEntry {
  dayOfWeek: number; // 0 = Sunday, 1 = Monday, etc.
  hour: number; // 0-23
  activeUsers: number;
  sessions: number;
}

/**
 * Fetch GA4 hourly activity data for the heatmap
 * Groups data by day of week and hour
 */
export function useGA4HourlyActivity() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;

  return useQuery({
    queryKey: ['ga4-hourly-activity', ga4PropertyId],
    queryFn: async (): Promise<HourlyActivityEntry[] | null> => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      try {
        const request = {
          propertyId: ga4PropertyId,
          dateRange: { startDate: '28daysAgo', endDate: 'yesterday' },
          dimensions: ['dayOfWeek', 'hour'],
          metrics: ['activeUsers', 'sessions'],
        };

        const response = await api.google.runGAReport(request);

        if (!response?.rows) {
          return [];
        }

        return response.rows.map((row: any) => ({
          dayOfWeek: parseInt(row.dimensionValues?.[0]?.value || '0', 10),
          hour: parseInt(row.dimensionValues?.[1]?.value || '0', 10),
          activeUsers: parseInt(row.metricValues?.[0]?.value || '0', 10),
          sessions: parseInt(row.metricValues?.[1]?.value || '0', 10),
        }));
      } catch (error) {
        console.error('Failed to fetch GA4 hourly activity:', error);
        return [];
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

interface DeviceBreakdown {
  device: string;
  users: number;
  sessions: number;
  percentage: number;
}

/**
 * Fetch GA4 device category breakdown
 */
export function useGA4DeviceBreakdown() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;

  return useQuery({
    queryKey: ['ga4-device-breakdown', ga4PropertyId],
    queryFn: async (): Promise<{ devices: DeviceBreakdown[]; totalUsers: number } | null> => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      try {
        const request = {
          propertyId: ga4PropertyId,
          dateRange: { startDate: '7daysAgo', endDate: 'yesterday' },
          dimensions: ['deviceCategory'],
          metrics: ['activeUsers', 'sessions'],
        };

        const response = await api.google.runGAReport(request);

        if (!response?.rows) {
          return { devices: [], totalUsers: 0 };
        }

        let totalUsers = 0;
        const devices = response.rows.map((row: any) => {
          const users = parseInt(row.metricValues?.[0]?.value || '0', 10);
          totalUsers += users;
          return {
            device: row.dimensionValues?.[0]?.value || 'unknown',
            users,
            sessions: parseInt(row.metricValues?.[1]?.value || '0', 10),
            percentage: 0, // Will be calculated after
          };
        });

        // Calculate percentages
        devices.forEach((d: DeviceBreakdown) => {
          d.percentage = totalUsers > 0 ? (d.users / totalUsers) * 100 : 0;
        });

        // Sort by users descending
        devices.sort((a: DeviceBreakdown, b: DeviceBreakdown) => b.users - a.users);

        return { devices, totalUsers };
      } catch (error) {
        console.error('Failed to fetch GA4 device breakdown:', error);
        return { devices: [], totalUsers: 0 };
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

interface TrafficSourceEntry {
  date: string;
  organic: number;
  direct: number;
  referral: number;
  social: number;
}

/**
 * Fetch GA4 traffic acquisition by source over time
 */
export function useGA4TrafficAcquisition() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;

  return useQuery({
    queryKey: ['ga4-traffic-acquisition', ga4PropertyId],
    queryFn: async (): Promise<TrafficSourceEntry[] | null> => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      try {
        const request = {
          propertyId: ga4PropertyId,
          dateRange: { startDate: '7daysAgo', endDate: 'yesterday' },
          dimensions: ['date', 'sessionDefaultChannelGroup'],
          metrics: ['sessions'],
          orderBy: { dimension: 'date' },
        };

        const response = await api.google.runGAReport(request);

        if (!response?.rows) {
          return [];
        }

        // Group by date
        const dateMap = new Map<string, TrafficSourceEntry>();

        for (const row of response.rows) {
          const date = row.dimensionValues?.[0]?.value || '';
          const channel = (row.dimensionValues?.[1]?.value || '').toLowerCase();
          const sessions = parseInt(row.metricValues?.[0]?.value || '0', 10);

          if (!dateMap.has(date)) {
            dateMap.set(date, {
              date,
              organic: 0,
              direct: 0,
              referral: 0,
              social: 0,
            });
          }

          const entry = dateMap.get(date)!;
          if (channel.includes('organic')) {
            entry.organic += sessions;
          } else if (channel.includes('direct')) {
            entry.direct += sessions;
          } else if (channel.includes('referral')) {
            entry.referral += sessions;
          } else if (channel.includes('social')) {
            entry.social += sessions;
          }
        }

        return Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
      } catch (error) {
        console.error('Failed to fetch GA4 traffic acquisition:', error);
        return [];
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch GA4 real-time active users (simulated with recent data)
 * Note: GA4 Data API doesn't have true real-time, this uses recent period
 */
export function useGA4ActiveUsers() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;

  return useQuery({
    queryKey: ['ga4-active-users', ga4PropertyId],
    queryFn: async (): Promise<number | null> => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      try {
        const request = {
          propertyId: ga4PropertyId,
          dateRange: { startDate: 'today', endDate: 'today' },
          metrics: ['activeUsers'],
        };

        const response = await api.google.runGAReport(request);
        return parseInt(response?.rows?.[0]?.metricValues?.[0]?.value || '0', 10);
      } catch (error) {
        console.error('Failed to fetch GA4 active users:', error);
        return 0;
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 1, // 1 minute - more frequent for "live" feel
    refetchInterval: 1000 * 60 * 1, // Auto-refresh every minute
  });
}
